import { FormattedMessage } from '@/components/formatted_message/FormattedMessage';
import * as Sentry from '@sentry/react';
import { Row, Space } from 'antd';
import type React from 'react';
import { type ComponentProps, type ReactNode, useEffect } from 'react';
import styled from 'styled-components';
import type { BugReportErrorDetails } from '@enmon/commons/lib/enmon';
import { useActiveCustomer, useActiveUser } from '@/containers/App';
import type { FallbackRender } from '@sentry/react';
import { ButtonAnt } from '../buttons/button/ButtonAnt';
import { CardAnt } from '../card';
import { useDialogWithExtraProps } from '../dialog/useDialogWithExtraProps';
import { ErrorBoundary as messages } from './messages';
import { BugReportModal, type BugReportModalProps } from './BugReport';

const StyledContainer = styled.div`
  padding: 36px;
`;

const ErrorFallback: React.FC<ComponentProps<FallbackRender>> = ({ resetError, error, componentStack }) => {
  const { dialog: bugReportDialog, openDialog: openBugReportDialog } =
    useDialogWithExtraProps<BugReportModalProps>(BugReportModal);

  const reactError = error as Error;

  const handleOpenBugReport = () => {
    const errorDetails: BugReportErrorDetails | undefined = reactError && {
      message: reactError.message,
      stack: reactError.stack,
      componentStack,
      name: reactError.name,
    };

    openBugReportDialog({ errorDetails });
  };

  return (
    <>
      {bugReportDialog}
      <StyledContainer>
        <Row justify="center">
          <CardAnt title={<FormattedMessage {...messages.header} />}>
            <Space align="end" direction="vertical">
              <div style={{ maxWidth: '576px' }}>
                <FormattedMessage {...messages.body} />
              </div>
              <Space size="middle">
                <ButtonAnt onClick={handleOpenBugReport}>
                  <FormattedMessage {...messages.report} />
                </ButtonAnt>
                <ButtonAnt type="primary" onClick={resetError}>
                  <FormattedMessage {...messages.refresh} />
                </ButtonAnt>
              </Space>
            </Space>
          </CardAnt>
        </Row>
      </StyledContainer>
    </>
  );
};

interface ErrorBoundaryProps {
  children: ReactNode;
  errorMessage?: ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, errorMessage }) => {
  const user = useActiveUser();
  const customer = useActiveCustomer();

  useEffect(() => {
    if (user) {
      Sentry.setTag('userId', user._id);
    }
    if (customer) {
      Sentry.setTag('customerId', customer._id);
      Sentry.setTag('customerName', customer.name);
    }
  }, [user, customer]);

  const handleReset = () => {
    window.location.reload();
  };

  const fallback: FallbackRender = props => (errorMessage ? <>{errorMessage}</> : <ErrorFallback {...props} />);

  return (
    <Sentry.ErrorBoundary fallback={fallback} onReset={handleReset}>
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
